import React, { useEffect, useId, useState } from 'react';
import { getEgressScanOptions } from '../../../services/egress.service';
import { toUserFacingErrorMessage } from '../../../services/_core';
import { IpList, REQUEST_DELAY_OPTIONS, UpgradeHint } from '../../settings/scan-network/scanNetworkShared';
import ProxyFields, { EMPTY_PROXY, proxyFieldsError, proxyPayload } from '../../settings/scan-network/ProxyFields';
import ProxyTestButton from '../../settings/scan-network/ProxyTestButton';
import { UsageLine } from '../../settings/scan-network/RotatingUsageMeter';
import '../../settings/scan-network/scan-network.css';

// Routes that depend on something that can be missing (an IP being prepared, an add-on
// that lapsed): only these need an "if it isn't available" choice.
const CAN_BE_UNAVAILABLE = new Set(['DEDICATED', 'STATIC_RESI', 'PROVIDER_POOL', 'BYO']);
const ONE_TIME = '__once';

/** IPs the target sees for a route: what the customer allow-lists. null = can't be allow-listed. */
export function allowlistFor(options, mode) {
  const ips = options?.allowlist_ips || {};
  const dedicated = ips.dedicated || [];
  const xspectre = ips.xspectre || [];
  const route = mode || options?.mode || 'AUTO';
  if (route === 'PROVIDER_POOL') return null;
  if (route === 'DEDICATED') return dedicated;
  if (route === 'STATIC_RESI') return ips.static_residential || [];
  if (route === 'PUBLIC_POOL') return xspectre;
  return dedicated.length ? dedicated : xspectre;
}

const delayLabel = (ms) => REQUEST_DELAY_OPTIONS.find((o) => o.value === ms)?.label || `${ms} ms between requests`;

/**
 * Scan manager "Network": which addresses this scan comes from, what happens if that route
 * isn't available, and how fast it sends requests. Blank values keep the organization's settings.
 * value: { egressMode: '' | mode, egressFallback: '' | 'STOP' | 'STANDARD', requestDelayMs: '' | number,
 *          egressProxy: '' | proxy id, egressProxyOnce: null | { scheme, host, port, username, password, useLogin },
 *          egressRotation: '' | 'scan' | 'request' }
 */
export default function ScanNetworkOptions({ orgId, value, onChange, disabled = false, isEditMode = false }) {
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [showIps, setShowIps] = useState(false);
  const idBase = useId();

  useEffect(() => {
    const id = String(orgId || '').trim();
    setOptions(null);
    setError('');
    if (!id) return undefined;
    let mounted = true;
    setLoading(true);
    getEgressScanOptions(id)
      .then((data) => mounted && setOptions(data || null))
      .catch((err) => mounted && setError(toUserFacingErrorMessage(err?.message, 'Network options could not be loaded.')))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [orgId, reloadKey]);

  const mode = value?.egressMode || '';
  const fallback = value?.egressFallback || '';
  const delay = value?.requestDelayMs ?? '';
  const set = (patch) => onChange({ ...value, ...patch });

  const products = Array.isArray(options?.products) ? options.products : [];
  const orgMode = options?.mode || 'AUTO';
  const effectiveMode = mode || orgMode;
  const orgModeLabel = options?.mode_label || 'Standard scanning';
  const chosenProduct = products.find((p) => p.value === mode);
  const proxies = Array.isArray(options?.proxies) ? options.proxies : [];
  const proxyChoice = value?.egressProxyOnce ? ONE_TIME : value?.egressProxy || '';
  const once = value?.egressProxyOnce || null;
  const chosenProxy = proxies.find((p) => p.id === value?.egressProxy);
  const orgDelay = Number(options?.scan_request_delay_ms) || 0;
  const fallbacks = Array.isArray(options?.fallbacks) ? options.fallbacks : [];
  const rotations = Array.isArray(options?.rotations) ? options.rotations : [];
  const rotation = value?.egressRotation || 'scan';
  const orgFallbackLabel = fallbacks.find((f) => f.value === options?.fallback)?.label || 'Stop the scan and let me know';
  const ips = options ? allowlistFor(options, mode) : null;

  const byoDetail = mode === 'BYO' ? (once ? ': entered for this scan' : chosenProxy ? `: ${chosenProxy.name}` : '') : '';
  const routeSummary = mode ? `${chosenProduct?.label || mode}${byoDetail} · this scan` : `${orgModeLabel} · organization setting`;
  const rateSummary = delay === '' ? `${delayLabel(orgDelay)} · organization setting` : `${delayLabel(Number(delay))} · this scan`;

  const locked = disabled || isEditMode;
  let body;
  if (!String(orgId || '').trim()) {
    body = <p className="settings-modal-card-hint">Select an organization first.</p>;
  } else if (loading) {
    body = <p className="settings-modal-card-hint" aria-busy="true">Loading network options…</p>;
  } else if (error || !options) {
    body = (
      <div className="scan-network-alert" role="status">
        {error || 'Network options are unavailable.'} This scan will use your organization&apos;s network settings.{' '}
        <button type="button" className="scan-network-link" onClick={() => setReloadKey((k) => k + 1)}>
          Try again
        </button>
      </div>
    );
  } else {
    body = (
      <>
        {!options.egress_enabled ? (
          <div className="scan-network-alert" role="status">
            Scan routing is switched off in this environment, so these choices aren&apos;t applied yet.
          </div>
        ) : null}

        <dl className="scan-network-summary">
          <div>
            <dt>Route</dt>
            <dd>{routeSummary}</dd>
          </div>
          <div>
            <dt>Request rate</dt>
            <dd>{rateSummary}</dd>
          </div>
        </dl>

        {isEditMode ? (
          <p className="settings-modal-card-hint">The route and request rate saved with this schedule are kept.</p>
        ) : (
          <button
            type="button"
            className="scan-network-link"
            aria-expanded={expanded}
            aria-controls={`${idBase}-network-panel`}
            onClick={() => setExpanded((open) => !open)}
            disabled={disabled}
          >
            {expanded ? 'Hide network options' : 'Change'}
          </button>
        )}

        {expanded && !isEditMode ? (
          <div id={`${idBase}-network-panel`} className="scan-network-panel">
            <fieldset className="scan-network-fieldset" disabled={locked}>
              <legend className="ghost-exposure-sys-field-label network-scan-manager-label">Route for this scan</legend>
              <div className="scan-network-modes" role="radiogroup" aria-label="Route for this scan">
                <label className={`scan-network-mode${mode === '' ? ' is-selected' : ''}`}>
                  <input type="radio" name={`${idBase}-route`} value="" checked={mode === ''} onChange={() => set({ egressMode: '' })} />
                  <span>
                    <span className="scan-network-mode-label">Use organization setting</span>
                    <span className="scan-network-mode-desc">{orgModeLabel}</span>
                  </span>
                </label>
                {products.map((product) => {
                  const selectable = product.available || product.value === 'BYO';
                  return (
                  <label
                    key={product.value}
                    className={`scan-network-mode${mode === product.value ? ' is-selected' : ''}${selectable ? '' : ' is-locked'}`}
                  >
                    <input
                      type="radio"
                      name={`${idBase}-route`}
                      value={product.value}
                      checked={mode === product.value}
                      disabled={!selectable}
                      onChange={() => set({ egressMode: product.value })}
                    />
                    <span>
                      <span className="scan-network-mode-label">{product.label}</span>
                      <span className="scan-network-mode-desc">{product.description}</span>
                      {!selectable ? (
                        <span className="scan-network-locked">
                          {product.locked_reason} <UpgradeHint product={product} />
                        </span>
                      ) : null}
                    </span>
                  </label>
                  );
                })}
              </div>
            </fieldset>

            {mode === 'BYO' ? (
              <div className="network-scan-manager-field">
                <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor={`${idBase}-proxy`}>
                  Proxy
                </label>
                <select
                  id={`${idBase}-proxy`}
                  className="ghost-exposure-sys-input network-scan-manager-select"
                  value={proxyChoice}
                  disabled={locked}
                  onChange={(e) => (e.target.value === ONE_TIME
                    ? set({ egressProxy: '', egressProxyOnce: once || { ...EMPTY_PROXY } })
                    : set({ egressProxy: e.target.value, egressProxyOnce: null }))}
                >
                  <option value="">Choose a proxy…</option>
                  {proxies.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  <option value={ONE_TIME}>Enter a proxy for this scan only</option>
                </select>
                {once ? (
                  <>
                    <ProxyFields value={once} onChange={(next) => set({ egressProxyOnce: next })} disabled={locked} />
                    <ProxyTestButton orgId={orgId} details={proxyPayload(once)} disabled={Boolean(proxyFieldsError(once))} />
                  </>
                ) : value?.egressProxy ? (
                  <ProxyTestButton orgId={orgId} proxyId={value.egressProxy} />
                ) : null}
              </div>
            ) : null}

            {mode === 'PROVIDER_POOL' && rotations.length ? (
              <fieldset className="scan-network-fieldset" disabled={locked}>
                <legend className="ghost-exposure-sys-field-label network-scan-manager-label">How often the IP changes</legend>
                <div className="scan-network-modes" role="radiogroup" aria-label="How often the IP changes">
                  {rotations.map((option) => (
                    <label key={option.value} className={`scan-network-mode${rotation === option.value ? ' is-selected' : ''}`}>
                      <input
                        type="radio"
                        name={`${idBase}-rotation`}
                        value={option.value}
                        checked={rotation === option.value}
                        onChange={() => set({ egressRotation: option.value })}
                      />
                      <span className="scan-network-mode-label">{option.label}</span>
                    </label>
                  ))}
                </div>
                <p className="settings-modal-card-hint">Billed by data used. These addresses change and can&apos;t be allow-listed.</p>
                <UsageLine usage={options.rotating_usage} />
              </fieldset>
            ) : null}

            {CAN_BE_UNAVAILABLE.has(effectiveMode) ? (
              <fieldset className="scan-network-fieldset" disabled={locked}>
                <legend className="ghost-exposure-sys-field-label network-scan-manager-label">If this route isn&apos;t available</legend>
                <div className="scan-network-modes" role="radiogroup" aria-label="If this route isn't available">
                  <label className={`scan-network-mode${fallback === '' ? ' is-selected' : ''}`}>
                    <input type="radio" name={`${idBase}-fallback`} value="" checked={fallback === ''} onChange={() => set({ egressFallback: '' })} />
                    <span>
                      <span className="scan-network-mode-label">Use organization setting</span>
                      <span className="scan-network-mode-desc">{orgFallbackLabel}</span>
                    </span>
                  </label>
                  {fallbacks.map((option) => (
                    <label key={option.value} className={`scan-network-mode${fallback === option.value ? ' is-selected' : ''}`}>
                      <input
                        type="radio"
                        name={`${idBase}-fallback`}
                        value={option.value}
                        checked={fallback === option.value}
                        onChange={() => set({ egressFallback: option.value })}
                      />
                      <span className="scan-network-mode-label">{option.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : null}

            <div className="network-scan-manager-field">
              <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor={`${idBase}-rate`}>
                Request rate
              </label>
              <select
                id={`${idBase}-rate`}
                className="ghost-exposure-sys-input network-scan-manager-select"
                value={delay === '' ? '' : String(delay)}
                onChange={(e) => set({ requestDelayMs: e.target.value === '' ? '' : Number(e.target.value) })}
                disabled={locked}
              >
                <option value="">Use organization setting ({delayLabel(orgDelay)})</option>
                {REQUEST_DELAY_OPTIONS.map((o) => (
                  <option key={o.value} value={String(o.value)}>
                    {o.label}
                  </option>
                ))}
              </select>
              <p className="settings-modal-card-hint">Slower is gentler on sites that rate-limit, and makes the scan take longer.</p>
            </div>

            <button
              type="button"
              className="scan-network-link"
              aria-expanded={showIps}
              onClick={() => setShowIps((open) => !open)}
            >
              {showIps ? 'Hide addresses to allow-list' : 'Addresses to allow-list'}
            </button>
            {showIps ? (
              ips === null ? (
                <p className="settings-modal-card-hint">Rotating residential addresses change on every scan and can&apos;t be allow-listed.</p>
              ) : (
                <>
                  <IpList
                    title="This scan comes from"
                    ips={ips}
                    emptyText="No scanning addresses are active yet. Contact xSPECTRE support before relying on an allow-list."
                  />
                  <p className="settings-modal-card-hint">
                    Every request carries the header <code>{options.scan_id_header || 'X-xSPECTRE-Scan-ID'}</code>.
                  </p>
                </>
              )
            ) : null}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <section className="settings-modal-card" aria-labelledby={`${idBase}-network-heading`}>
      <div className="settings-modal-card-head">
        <h3 id={`${idBase}-network-heading`} className="settings-modal-card-title">
          Network
        </h3>
        <p className="settings-modal-card-hint">Where this scan comes from and how fast it sends requests.</p>
      </div>
      <div className="settings-modal-card-body">{body}</div>
    </section>
  );
}
